import { Injectable, OnDestroy } from '@angular/core'
import { DefaultService, UserOverview, UserUpdate } from 'src/app/ops-aware-users-api'
import { Observable, map, firstValueFrom, ReplaySubject, Subscription } from 'rxjs'
import { UserAuthService } from '../userAuth/user-auth.service'

/**
 * Service responsible for retrieving and updating information about the currently logged-in user.
 * This service listens for authentication state changes and ensures the user information is kept up-to-date.
 */
@Injectable({
    providedIn: 'root'
})
export class LoggedInUserService implements OnDestroy
{
    // ReplaySubject to store the latest user overview data and broadcast changes
    private userSettings = new ReplaySubject<UserOverview>(1)

    // Subscription to monitor token changes and trigger user data refresh
    private userSessionSubscription: Subscription

    /**
     * Returns an observable indicating whether the terms and conditions should be shown to the user.
     */
    get shouldShowTermsAndConditions(): Observable<boolean>
    {
        return this.overview().pipe(
            map((overview) =>
            {
                return !overview.acceptedTerms
            })
        )
    }

    /**
     * Updates the user's acceptance of terms and conditions.
     * Triggers a settings refresh after the update.
     *
     * @param hasAgreed - Whether the user has agreed to the terms.
     */
    async updateUserAgreement(hasAgreed: boolean)
    {
        await this.setUserSettings({
            acceptedTerms: hasAgreed
        })

        await this.requestSettingsRefresh()
    }

    /**
     * Updates user settings with new values and triggers a settings refresh.
     *
     * @param settings - The updated user settings.
     */
    async setUserSettings(settings: UserUpdate)
    {
        await firstValueFrom(this.usersAPI.updateUser(settings))

        await this.requestSettingsRefresh()
    }

    /**
     * Returns an observable stream of the current user overview data.
     * This allows components to reactively listen to changes.
     */
    overview(): Observable<UserOverview>
    {
        return this.userSettings.asObservable()
    }

    /**
     * Returns an observable indicating whether the logged-in user has admin privileges
     * in any associated company.
     */
    isCompanyAdmin(): Observable<boolean>
    {
        return this.overview().pipe(
            map((overview) =>
            {
                for (let companyID in overview.associations)
                {
                    if (overview.associations[companyID].role == 'admin')
                    {
                        return true
                    }
                }

                return false
            })
        )
    }

    /**
     * Retrieves the latest user overview data from the backend and updates the local cache.
     */
    private async requestSettingsRefresh()
    {
        const user = await firstValueFrom(this.usersAPI.getAuthenticatedUser())

        this.userSettings.next(user)
    }

    /**
     * Clears the current user settings and prepares the service for a new session.
     */
    clearSettings()
    {
        this.userSettings.complete()
        this.userSettings = new ReplaySubject(1)
    }

    /**
     * Initializes the service by subscribing to the authentication token observable.
     * Whenever the token changes, this either refreshes the user settings (if authenticated) or reloads the page (if logged out).
     */
    constructor(
        private usersAPI: DefaultService,
        private userAuth: UserAuthService
    )
    {
        this.userSessionSubscription = this.userAuth.token().subscribe((token) =>
        {
            if (!token)
            {
                // No valid token found, reload the page to reset state and redirect to login
                window.location.reload()
            }
            else
            {
                // Valid token found, refresh user settings
                this.requestSettingsRefresh()
            }
        })
    }

    /**
     * Cleans up resources when the service is destroyed, ensuring no memory leaks.
     */
    ngOnDestroy()
    {
        this.userSessionSubscription.unsubscribe()
    }
}